import React from 'react';
import ChartCard from '@app/component/chart/ChartCard';
import DataTable, { DataTableRow } from '@app/component/chart/DataTable';
import { RevenueScope, SegmentationReport, SegmentationRow, VatBasis } from '@app/model/bi/Bi';
import { formatMonthRange, MonthRange } from '@ui/selector/MonthRangePicker';
import { scopeRulesNote } from '@app/model/integration-settings/ReportRules';
import { formatDate, missingNote, missingOf, revenueContent, revenueName } from '../Bi.utilities';
import { cellsOf, ColumnSet, columnsOf, figuresOf, situation } from './SegmentationTable.utilities';

export type { ColumnSet } from './SegmentationTable.utilities';

type Props = {
	report: SegmentationReport | undefined,
	// The stay months of the table.
	period: MonthRange,
	columnSets: ColumnSet[],
	// 'refreshing': a new period, view or hotel loads; the old table shows dimmed under its own title.
	state: 'default' | 'loading' | 'refreshing' | 'error',
	onRetry: () => void,
	asOf: string,
	// The revenue switches of the page (ROO2-236): they select Rev and ADR.
	scope: RevenueScope,
	vat: VatBasis,
	// ROO2-315 part c1: the properties of the selection, for the note of the report rules.
	properties: number,
	// ROO2-302 (Q1 C): the note that the held rooms of group blocks are not in this report yet (heldBlocksNote); null without them.
	heldNote?: string | null,
	// The controls in the header of the card: the period and the columns.
	actions?: React.ReactNode,
};

// The table of the report: one row per group (it opens to its PMS segments) or per PMS segment, "Not grouped" and
// "No segment" last and gray, and the total. Situation D-n = on the books at the end of the day n days ago.
const SegmentationTableCard: React.FunctionComponent<Props> = ({ report, period, columnSets, state, onRetry, asOf, scope, vat, properties, heldNote = null, actions }) => {
	const groupsView = report?.view !== 'SEGMENTS';
	const { columns, groups } = columnsOf(columnSets, groupsView ? 'Group' : 'PMS segment');
	const total = report === undefined ? 0 : situation(report.total, 0).roomNights;
	const toRow = (row: SegmentationRow): DataTableRow => ({
		id: row.key,
		cells: cellsOf(row, columnSets, total, scope, vat),
		muted: row.kind === 'NOT_GROUPED' || row.kind === 'NO_SEGMENT',
		note: row.kind === 'GROUP' || row.kind === 'NOT_GROUPED' ? `· ${row.segments.length}` : undefined,
		children: row.segments.length > 0 ? row.segments.map(toRow) : undefined,
	});
	const note = report === undefined ? null
		: missingNote(missingOf([...report.rows.flatMap(figuresOf), ...figuresOf(report.total)], scope, vat), '–', 'the figure');
	// ROO2-315 part c1 (Q4): the report rules, once, for a selection with rules; ADR then divides by the occupied rooms.
	const rulesNote = report === undefined ? null : scopeRulesNote(report.reportRules, report.rulesDiffer, properties);
	const empty = report !== undefined && total === 0 && report.rows.every(r => r.situations.every(s => s.roomNights === 0));

	// The title follows the table on the screen: the months of the report, not of the control.
	const periodName = formatMonthRange(report === undefined ? period : { from: report.stayFrom.slice(0, 7), to: report.stayTo.slice(0, 7) });
	return <ChartCard title={`${groupsView ? 'Groups' : 'PMS segments'} · ${periodName}`}
		period={report === undefined ? undefined : `Stay dates ${formatDate(report.stayFrom)} – ${formatDate(report.stayTo)} · past nights actual, later nights on the books`}
		state={state !== 'loading' && state !== 'error' && empty ? 'empty' : state} onRetry={onRetry}
		emptyLabel="No sold room nights in these stay months."
		actions={<div className="flex flex-wrap items-center gap-2">{actions}</div>}
		caption={<>
			{`RN = sold room nights; Rev = ${revenueName(scope, vat)} (${revenueContent(scope)}). Situation D-n = on the books at the end of the day n days before ${formatDate(asOf)} (hotel time), from the book and cancel dates. PU n = today − situation D-n.`}
			{note !== null && <><br />{note}</>}
			{rulesNote !== null && <><br />{`ADR = Rev ÷ occupied rooms. ${rulesNote}`}</>}
			{heldNote !== null && <><br />{heldNote}</>}
		</>}>
		{report !== undefined && <DataTable columns={columns} columnGroups={groups} ariaLabel={`Segmentation, ${periodName}`}
			rows={report.rows.map(toRow)} footer={{ id: 'total', cells: cellsOf(report.total, columnSets, total, scope, vat) }} framed={false} />}
	</ChartCard>;
};

export default SegmentationTableCard;
